import { createSlice } from '@reduxjs/toolkit';
import { DUPLICATE_ROW_COLORS, IPendingRequest } from 'constants/pending-requests.constants';
import { pendingRequestsTypes } from 'constants/requests.constants';
import { userMessage } from 'constants/ui.constants';
import {
	approveFeature,
	getBoSignerAllBalanceRequests,
	getBoSignerBalanceRequestsByStatus,
	getFeature,
	updateDigitalBalanceDocumentedInHyper,
	getPendingRequestsByType,
} from 'services/pending-requests.service';

interface IPaginatedRequestData {
	data: any[];
	pagination: { total: number; hasMore: boolean };
	loading: boolean;
}

interface IPendingRequestsState {
	loading: boolean;
	error: userMessage;
	pendingRequestsAnalytics: IPendingRequest | null;
	requests?: Record<pendingRequestsTypes, any>;
	paginatedRequests: Record<string, IPaginatedRequestData>;
	boSignerPendingRequests?: any;
	boSignerBalanceRequestsByStatus?: {
		[status: string]: {
			data: any[];
			pagination: { total: number; hasMore: boolean };
		};
	};
	/** Maps companyId to color for duplicate balance-addition rows (manual vs digital cross-check) */
	duplicateColorMap: Record<number, string>;
	nextColorIndex: number;
}

const initialState: IPendingRequestsState = {
	loading: false,
	error: { exist: false },
	pendingRequestsAnalytics: null,
	paginatedRequests: {},
	boSignerPendingRequests: null,
	boSignerBalanceRequestsByStatus: {},
	duplicateColorMap: {},
	nextColorIndex: 0,
};

export const pendingRequestsSlice = createSlice({
	name: 'pendingRequests',
	initialState,
	extraReducers: (builder) => {
		builder
			.addCase(approveFeature.pending, (state) => {
				state.loading = true;
			})
			.addCase(approveFeature.fulfilled, (state, { payload }) => {
				state.loading = false;
				state.requests = payload;
			})
			.addCase(approveFeature.rejected, (state, action) => {
				state.loading = false;
			})
			.addCase(getBoSignerAllBalanceRequests.pending, (state) => {
				state.loading = true;
			})
			.addCase(getBoSignerAllBalanceRequests.fulfilled, (state, { payload }) => {
				state.loading = false;
				state.boSignerPendingRequests = payload.balanceRequests;
			})
			.addCase(getBoSignerAllBalanceRequests.rejected, (state, action) => {
				state.loading = false;
			})
			.addCase(getBoSignerBalanceRequestsByStatus.fulfilled, (state, { payload }) => {
				if (payload && payload.status && payload.data) {
					if (!state.boSignerBalanceRequestsByStatus) {
						state.boSignerBalanceRequestsByStatus = {};
					}
					state.boSignerBalanceRequestsByStatus[payload.status] = {
						data: payload.data.result || [],
						pagination: payload.data.pagination || { total: 0, hasMore: false },
					};
				}
			})
			.addCase(getFeature.pending, (state) => {
				state.loading = true;
			})
			.addCase(getFeature.fulfilled, (state, { payload }) => {
				state.loading = false;
				state.requests = payload;
			})
			.addCase(getFeature.rejected, (state, action) => {
				state.loading = false;
			})
			.addCase(updateDigitalBalanceDocumentedInHyper.pending, (state) => {
				state.loading = true;
			})
			.addCase(updateDigitalBalanceDocumentedInHyper.fulfilled, (state, { payload }) => {
				state.loading = false;
				if (payload) {
					state.requests = payload;
				}
			})
			.addCase(updateDigitalBalanceDocumentedInHyper.rejected, (state, action) => {
				state.loading = false;
			})
			// Paginated pending requests - dynamic case for all types
			.addCase(getPendingRequestsByType.pending, (state, action) => {
				const type = action.meta.arg.type;
				if (!state.paginatedRequests[type]) {
					state.paginatedRequests[type] = {
						data: [],
						pagination: { total: 0, hasMore: false },
						loading: false,
					};
				}
				state.paginatedRequests[type].loading = true;
			})
			.addCase(getPendingRequestsByType.fulfilled, (state, { payload }) => {
				if (payload && payload.type && payload.data) {
					const rows = payload.data.result || [];
					// Update duplicate color map for balance tables (manual + digital)
					if (
						payload.type === pendingRequestsTypes.BALANCES ||
						payload.type === pendingRequestsTypes.DIGITAL_BALANCES_REQUESTS
					) {
						// Assign a color to each new duplicate company. Same companyId gets same color
						// across both tables. Only add if companyId is not already in the map.
						rows.forEach((row: { hasDuplicate?: boolean; companyId?: number }) => {
							if (
								row.hasDuplicate === true &&
								typeof row.companyId === 'number' &&
								!(row.companyId in state.duplicateColorMap)
							) {
								state.duplicateColorMap[row.companyId] =
									DUPLICATE_ROW_COLORS[state.nextColorIndex % DUPLICATE_ROW_COLORS.length];
								state.nextColorIndex += 1;
							}
						});
					}
					state.paginatedRequests[payload.type] = {
						data: rows,
						pagination: payload.data.pagination || { total: 0, hasMore: false },
						loading: false,
					};
				}
			})
			.addCase(getPendingRequestsByType.rejected, (state, action) => {
				const type = action.meta.arg.type;
				if (state.paginatedRequests[type]) {
					state.paginatedRequests[type].loading = false;
				}
			});
	},
	reducers: {
		setPendingRequestsAnalytics: (state, { payload }) => {
			state.pendingRequestsAnalytics = payload;
		},
		setLoading: (state, { payload }) => {
			state.loading = payload;
		},
		setError: (state, { payload }) => {
			state.error = payload;
		},
		clearBoSignerRequests: (state) => {
			state.boSignerPendingRequests = null;
			state.boSignerBalanceRequestsByStatus = {};
		},
		clearPaginatedRequests: (state) => {
			state.paginatedRequests = {};
		},
		clearDuplicateColorMap: (state) => {
			state.duplicateColorMap = {};
			state.nextColorIndex = 0;
		},
	},
});

export const getBoSignerPendingRequests = (state: any) => {
	return state.pendingRequests.boSignerPendingRequests;
};

export const getBoSignerBalanceRequestsByStatusData = (state: any, status: string) => {
	return (
		state.pendingRequests.boSignerBalanceRequestsByStatus?.[status] || {
			data: [],
			pagination: { total: 0, hasMore: false },
		}
	);
};
export const getPendingRequestsAnalytics = (state: any) => {
	return state.pendingRequests.pendingRequestsAnalytics;
};

export const isLoading = (state: any) => {
	return state.pendingRequests.loading;
};

export const getError = (state: any) => {
	return state.pendingRequests.error;
};

// Selector for paginated pending requests by type
export const getPaginatedRequests = (state: any, type: string) => {
	return (
		state.pendingRequests.paginatedRequests?.[type] || {
			data: [],
			pagination: { total: 0, hasMore: false },
			loading: false,
		}
	);
};

export const getDuplicateColorMap = (state: any) =>
	state.pendingRequests.duplicateColorMap || {};

export const {
	setPendingRequestsAnalytics,
	setLoading,
	setError,
	clearBoSignerRequests,
	clearPaginatedRequests,
	clearDuplicateColorMap,
} = pendingRequestsSlice.actions;

export default pendingRequestsSlice.reducer;
